import React, { useState } from "react";
import TransitionWrapper from "../common/transition-wrapper";
import { gql } from "../../__generated__";
import { useQuery } from "@apollo/client";
import AppDownloadModal from "../app-download/app-download-modal";
import { isWeb } from "../../utils/user-agent";
import breadImage from "./images/bread.png";
import kimbabImage from "./images/kimbab.png";
import donutImage from "./images/donut.png";
import { FaBell } from "react-icons/fa";

const GET_MENUS_BY_STORE_ID = gql(`
  query menusByStoreId($storeId: Int!) {
    menusByStoreId(storeId: $storeId) {
      id
      price
    }
  }
`);

const GET_PRODUCTS_BY_STORE_ID = gql(`
  query products($storeId: Int!) {
    products(storeId: $storeId) {
      id
      price
      createdAt
    }
  }
`);

export const NoProduct = ({
  reviewImg,
  reviewContent,
  storeId,
  storeTitle,
  onClickLike,
  isLiked,
}: {
  reviewImg: string | null | undefined;
  reviewContent: string | null | undefined;
  storeTitle: string | null | undefined;
  storeId: number;
  onClickLike?: (
    storeId: number,
    isLiked: boolean | null,
    storeTitle?: string
  ) => Promise<void>;
  isLiked?: boolean | null;
}): JSX.Element => {
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);

  const { data: menusData } = useQuery(GET_MENUS_BY_STORE_ID, {
    variables: {
      storeId,
    },
  });
  const { data: productsData } = useQuery(GET_PRODUCTS_BY_STORE_ID, {
    variables: {
      storeId,
    },
  });

  const menus = menusData?.menusByStoreId || [];

  const products = productsData?.products || [];

  const discountAmount =
    products.length >= 2 ? (products[0]?.price * 0.4).toFixed(0) : null;
  const discountAmount2 =
    menus.length >= 2
      ? ((menus[0]?.price + menus[1]?.price) * 0.4).toFixed(0)
      : null;

  const formatProductTime = (products: { createdAt: string }[]) => {
    if (products.length === 0) return null;

    const sortedProducts = [...products].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      const timeA = dateA.getHours() * 60 + dateA.getMinutes();
      const timeB = dateB.getHours() * 60 + dateB.getMinutes();
      return timeA - timeB;
    });

    let earliestProductDate = new Date(sortedProducts[0].createdAt);
    let latestProductDate = new Date(
      sortedProducts[sortedProducts.length - 1].createdAt
    );

    const earliestHours = earliestProductDate.getHours();
    const earliestMinutes = earliestProductDate.getMinutes();
    const latestHours = latestProductDate.getHours();
    const latestMinutes = latestProductDate.getMinutes();

    const timeDifference =
      (latestHours * 60 +
        latestMinutes -
        (earliestHours * 60 + earliestMinutes)) /
      60;

    // 4시간 이상 차이가 나면 마지막 시간에서 2시간을 뺌
    if (timeDifference >= 4) {
      earliestProductDate.setHours(latestProductDate.getHours() - 2);
    }

    const formatTime = (date: Date) => {
      let hours = date.getHours();
      let minutes = date.getMinutes();

      // 1의 자리 분을 올림 처리
      if (minutes % 10 !== 0) {
        minutes = Math.ceil(minutes / 10) * 10;
        if (minutes === 60) {
          minutes = 0;
          hours += 1;
        }
      }

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    };

    return `${formatTime(earliestProductDate)} ~ ${formatTime(
      latestProductDate
    )}`;
  };

  const handleAppDownloadModalClose = () => {
    setShowAppDownloadModal(false);
  };

  const handleNotificationClick = async () => {
    if (isWeb()) {
      setShowAppDownloadModal(true);
      return;
    }
    if (onClickLike) {
      await onClickLike(storeId, isLiked || false, storeTitle || undefined);
    }
  };

  return (
    <div className="w-full px-5 pb-10">
      <div className="space-y-5">
        {/* 상품 이미지 섹션 */}
        <div className="flex gap-3">
          {[breadImage, kimbabImage, donutImage].map((image, index) => (
            <div
              key={index}
              className="relative flex-1 aspect-square rounded-xl overflow-hidden"
            >
              <img
                src={image}
                alt={`Product ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-semibold text-sm text-center px-2">
                  판매중이 아니에요
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* 픽업 시간 섹션 */}
        <div className="bg-blue-50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#2563eb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-gray-700 mb-1 font-medium">언제 올라오나요?</p>
              {products.length !== 0 && formatProductTime(products) ? (
                <>
                  <span className="text-blue-600 font-semibold text-lg">
                    {formatProductTime(products)}
                  </span>
                  <span className="ml-1 text-sm text-gray-500 mt-1">
                    쯤 올라와요
                  </span>
                </>
              ) : (
                <p className="text-gray-500 text-sm mt-1">
                  입점한 지 얼마 안 되어 정보가 부족해요!
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 할인 정보 섹션 */}
        {(discountAmount || discountAmount2) && (
          <div className="bg-amber-50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#d97706"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-gray-700 mb-1 font-medium">한 번 구매시</p>

                <div className="mt-1">
                  <span className="text-blue-600 text-xl font-bold">
                    {Math.max(
                      discountAmount ? parseInt(discountAmount) : 0,
                      discountAmount2 ? parseInt(discountAmount2) : 0
                    ).toLocaleString()}
                    원
                  </span>
                  <span className="ml-1 text-gray-600 text-sm">
                    을 절약해요!
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 하단 버튼 (이미 받는 상태에선 노출 ❌)*/}
        {onClickLike && !isLiked && (
          <div className="pt-2">
            <TransitionWrapper
              onClick={handleNotificationClick}
              scale={0.95}
              opacity={0.8}
              className="w-full py-4 rounded-xl bg-[#1562fc] text-white font-bold text-center transition-colors flex items-center justify-center gap-2"
            >
              <FaBell className="text-sm" />
              오픈 알림받기
            </TransitionWrapper>
          </div>
        )}
      </div>

      <AppDownloadModal
        isOpen={showAppDownloadModal}
        onClose={handleAppDownloadModalClose}
        where="스토어 상품 없음 페이지"
      />
    </div>
  );
};

export default NoProduct;
